import type {
  InvoiceDetailResponseDto,
  InvoiceLineItemResponseDto,
  InvoiceNumberAvailabilityResponseDto,
  InvoiceResponseDto,
  InvoicesControllerListInvoicesParams,
  NextInvoiceNumberResponseDto,
  PaymentReceiptResponseDto,
  PaymentReceiptUrlsResponseDto,
  PaymentResponseDto,
  ReissueInvoiceDto,
} from '@trellis/api'
import type {
  BatchUpdateInvoiceLineItems,
  CreateInvoice,
  DuplicateInvoice,
  InvoiceStatus,
  RecordPayment,
  RequestResult,
  UpdateInvoice,
  UpdateInvoiceLineItem,
} from '@trellis/shared'

import { env } from '@/env'
import { apiClient } from '@/lib/api-client'
import { request, requestPaged } from '@/lib/request'
import { getStoredTenantSlug } from '@/lib/tenant'

export const listInvoices = (params: InvoicesControllerListInvoicesParams = {}) =>
  requestPaged<InvoiceResponseDto>(() =>
    apiClient.api.invoicesControllerListInvoices(params),
  )

export const getInvoice = (id: string) =>
  request<InvoiceDetailResponseDto>(() =>
    apiClient.api.invoicesControllerGetInvoice({ id }),
  )

export const createInvoice = (data: CreateInvoice) =>
  request<InvoiceResponseDto>(() => apiClient.api.invoicesControllerCreateInvoice(data))

export const updateInvoice = (id: string, data: UpdateInvoice) =>
  request<InvoiceResponseDto>(() =>
    apiClient.api.invoicesControllerUpdateInvoice({ id }, data),
  )

export const deleteInvoice = (id: string) =>
  request<void>(() => apiClient.api.invoicesControllerDeleteInvoice({ id }))

export const duplicateInvoice = (id: string, data?: DuplicateInvoice) =>
  request<InvoiceResponseDto>(() =>
    apiClient.api.invoicesControllerDuplicateInvoice({ id }, data),
  )

export const reissueInvoice = (id: string, data?: ReissueInvoiceDto) =>
  request<InvoiceResponseDto>(() =>
    apiClient.api.invoicesControllerReissueInvoice({ id }, data),
  )

export const linkReissuedInvoice = (id: string, invoiceId: string) =>
  request<InvoiceDetailResponseDto>(() =>
    apiClient.api.invoicesControllerLinkReissuedInvoice({ id }, { invoiceId }),
  )

export const unlinkReissuedInvoice = (id: string) =>
  request<void>(() => apiClient.api.invoicesControllerUnlinkReissuedInvoice({ id }))

export const getNextInvoiceNumber = () =>
  request<NextInvoiceNumberResponseDto>(() =>
    apiClient.api.invoicesControllerGetNextInvoiceNumber(),
  )

export const checkInvoiceNumberAvailability = (invoiceNumber: string) =>
  request<InvoiceNumberAvailabilityResponseDto>(() =>
    apiClient.api.invoicesControllerGetInvoiceNumberAvailability({ invoiceNumber }),
  )

export const transitionInvoiceStatus = (id: string, status: InvoiceStatus) =>
  request<InvoiceResponseDto>(() =>
    apiClient.api.invoicesControllerTransitionInvoiceStatus({ id }, { status }),
  )

export const archiveInvoice = (id: string) =>
  request<InvoiceResponseDto>(() =>
    apiClient.api.invoicesControllerArchiveInvoice({ id }),
  )

export const unarchiveInvoice = (id: string) =>
  request<InvoiceResponseDto>(() =>
    apiClient.api.invoicesControllerUnarchiveInvoice({ id }),
  )

export const batchUpdateInvoiceLineItems = (
  invoiceId: string,
  data: BatchUpdateInvoiceLineItems,
) =>
  request<InvoiceLineItemResponseDto[]>(() =>
    apiClient.api.invoiceLineItemsControllerBatchUpdateLineItems({ invoiceId }, data),
  )

export const updateInvoiceLineItem = (
  invoiceId: string,
  id: string,
  data: UpdateInvoiceLineItem,
) =>
  request<InvoiceLineItemResponseDto>(() =>
    apiClient.api.invoiceLineItemsControllerUpdateInvoiceLineItem(
      { invoiceId, id },
      data,
    ),
  )

export const deleteInvoiceLineItem = (invoiceId: string, id: string) =>
  request<void>(() =>
    apiClient.api.invoiceLineItemsControllerDeleteInvoiceLineItem({ invoiceId, id }),
  )

export const syncInvoiceLineItem = (invoiceId: string, id: string) =>
  request<InvoiceLineItemResponseDto>(() =>
    apiClient.api.invoiceLineItemsControllerSyncInvoiceLineItem({ invoiceId, id }),
  )

export const syncAllInvoiceLineItems = (invoiceId: string) =>
  request<InvoiceLineItemResponseDto[]>(() =>
    apiClient.api.invoiceLineItemsControllerSyncAllInvoiceLineItems({ invoiceId }),
  )

export async function getInvoicePreview(id: string): Promise<RequestResult<string>> {
  try {
    const tenantSlug = getStoredTenantSlug()
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/invoices/${id}/preview`, {
      credentials: 'include',
      headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : undefined,
    })
    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: { code: 'PREVIEW_ERROR', message: 'Failed to load invoice preview' },
      }
    }
    const html = await response.text()
    return { status: 200, data: html, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'An unexpected error occurred',
      },
    }
  }
}

export const assignTagsToInvoice = (invoiceId: string, tagIds: string[]) =>
  request<void>(() =>
    apiClient.api.invoicesControllerAssignTags({ id: invoiceId }, { tagIds }),
  )

export const removeTagFromInvoice = (invoiceId: string, tagId: string) =>
  request<void>(() => apiClient.api.invoicesControllerRemoveTag({ id: invoiceId, tagId }))

export const recordPayment = (invoiceId: string, data: RecordPayment) =>
  request<PaymentResponseDto>(() =>
    apiClient.api.invoicesControllerRecordPayment({ id: invoiceId }, data),
  )

export const deletePayment = (invoiceId: string, paymentId: string) =>
  request<void>(() =>
    apiClient.api.invoicesControllerDeletePayment({ id: invoiceId, paymentId }),
  )

export const uploadPaymentReceipt = async (
  invoiceId: string,
  paymentId: string,
  file: File,
): Promise<RequestResult<PaymentReceiptResponseDto>> => {
  const formData = new FormData()
  formData.append('file', file)

  const headers = new Headers({ Accept: 'application/json' })
  const slug = getStoredTenantSlug()
  if (slug) headers.set('X-Tenant-Slug', slug)

  try {
    const response = await fetch(
      `${env.VITE_API_BASE_URL}/api/invoices/${invoiceId}/payments/${paymentId}/receipt`,
      { method: 'POST', body: formData, credentials: 'include', headers },
    )
    if (!response.ok) {
      const body = await response.json().catch(() => null)
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: body?.error?.code ?? 'API_ERROR',
          message: body?.error?.message ?? 'Upload failed',
        },
      }
    }
    const body = await response.json()
    return { status: response.status, data: body.data, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 0,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message: caughtError instanceof Error ? caughtError.message : 'Upload failed',
      },
    }
  }
}

export const getPaymentReceiptUrl = (invoiceId: string, paymentId: string) =>
  request<PaymentReceiptUrlsResponseDto>(() =>
    apiClient.api.invoicesControllerGetPaymentReceipt({ id: invoiceId, paymentId }),
  )

export const deletePaymentReceipt = (invoiceId: string, paymentId: string) =>
  request<void>(() =>
    apiClient.api.invoicesControllerDeletePaymentReceipt({ id: invoiceId, paymentId }),
  )
